import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Card, Col, Row, notification } from 'antd'

import addMigrationQueue, { AddMigrationQueueRequest } from 'core/application/csiDashboard/migration/addMigrationQueue'
import getMigrationLogs, {
  MigrationLogsFilter,
  PagedMigrationLog,
} from 'core/application/csiDashboard/migration/getMigrationLogs'
import getMigrationQueue, {
  MigrationQueueFilter,
  PagedMigrationQueue,
} from 'core/application/csiDashboard/migration/getMigrationQueue'

import RowWithAction from 'common/components/dataDisplay/RowWithAction'

import { commandRequest, queryRequest } from 'common/RequestUtils'

import MigrationLogsTable from './MigrationLogsTable'
import MigrationQueueForm from './MigrationQueueForm'
import MigrationQueueResultsTable from './MigrationQueueResultsTable'
import SearchFields from './SearchFields'

import './styles.less'

export const MIGRATION_PATH = 'migration'

const defaultFilter: MigrationQueueFilter = {
  pageNumber: 1,
  pageSize: 20,
  orderColumn: 'migrationDate',
  orderDescending: true,
  companyId: '',
  migrationDate: null,
}

const defaultLogsFilter: MigrationLogsFilter = {
  pageNumber: 1,
  pageSize: 20,
  orderColumn: 'migrationDate',
  orderDescending: false,
  companyId: '',
}

const Migration: React.FC = () => {
  const { t: translate } = useTranslation()
  const [searchFilter, setSearchFilter] = useState<MigrationQueueFilter>(defaultFilter)
  const [migrationQueueSearchValue, setMigrationQueueSearchValue] = useState<any>()
  const [loadingMigrationQueueResults, setLoadingMigrationQueueResults] = useState<boolean>(false)
  const [migrationQueueResults, setMigrationQueueResults] = useState<PagedMigrationQueue>()
  const [logsSearchFilter, setLogsSearchFilter] = useState<MigrationLogsFilter>(defaultLogsFilter)
  const [migrationLogsSearchValue, setMigrationLogsSearchValue] = useState<any>()
  const [migrationLogsResults, setMigrationLogsResults] = useState<PagedMigrationLog>()
  const [loadingMigrationLogsResults, setLoadingMigrationLogsResults] = useState<boolean>(false)
  const [showForm, setShowForm] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)

  const updateMigrationQueueResultTable = async () => {
    setLoadingMigrationQueueResults(true)

    const result = await queryRequest(() => getMigrationQueue(searchFilter!))
    setMigrationQueueResults(result)

    setLoadingMigrationQueueResults(false)
  }

  const updateMigrationLogsTable = async () => {
    setLoadingMigrationLogsResults(true)

    const result = await queryRequest(() => getMigrationLogs(logsSearchFilter!))
    setMigrationLogsResults(result)

    setLoadingMigrationLogsResults(false)
  }

  useEffect(() => {
    updateMigrationQueueResultTable()
    setSearchFilter(searchFilter)
  }, [migrationQueueSearchValue])

  useEffect(() => {
    if (migrationLogsSearchValue) updateMigrationLogsTable()
    setLogsSearchFilter(logsSearchFilter)
  }, [migrationLogsSearchValue])

  const onSearch = (searchReq: any) => {
    setMigrationQueueSearchValue(searchReq)
    searchFilter.companyId = searchReq.companyId
    searchFilter.migrationDate = searchReq.migrationDate
    searchFilter.pageNumber = 1
    searchFilter.pageSize = 20
    searchFilter.orderColumn = 'migrationDate'
    searchFilter.orderDescending = true
    setSearchFilter(searchFilter)
  }

  const onTableChange = (obj: any) => {
    const searchReq = {
      companyId: migrationQueueSearchValue.companyId,
      migrationDate: migrationQueueSearchValue.migrationDate,
    }
    setMigrationQueueSearchValue(searchReq)

    if (obj.pageNumber) searchFilter.pageNumber = obj.pageNumber
    if (obj.pageSize) searchFilter.pageSize = obj.pageSize
    if (obj.orderColumn) searchFilter.orderColumn = obj.orderColumn
    if (obj.orderDescending) searchFilter.orderDescending = obj.orderDescending
    setSearchFilter(searchFilter)
  }

  const onCompanySelect = (companyId: any) => {
    setMigrationLogsSearchValue(companyId)
    logsSearchFilter.companyId = companyId
    logsSearchFilter.pageNumber = 1
    logsSearchFilter.pageSize = 20
    logsSearchFilter.orderColumn = 'logDate'
    logsSearchFilter.orderDescending = false
    setLogsSearchFilter(logsSearchFilter)
  }

  const onLogsTableChange = (obj: any) => {
    const searchReq = {
      companyId: migrationLogsSearchValue.companyId,
    }
    setMigrationLogsSearchValue(searchReq)

    if (obj.pageNumber) logsSearchFilter.pageNumber = obj.pageNumber
    if (obj.pageSize) logsSearchFilter.pageSize = obj.pageSize
    if (obj.orderColumn) logsSearchFilter.orderColumn = obj.orderColumn
    if (obj.orderDescending) logsSearchFilter.orderDescending = obj.orderDescending
    setLogsSearchFilter(logsSearchFilter)
  }

  const onAddMigrationClick = async () => {
    setShowForm(true)
  }

  const submitForm = async (values: AddMigrationQueueRequest) => {
    setSubmitting(true)

    const result = await commandRequest(() => addMigrationQueue(values))
    if (!result.errors) {
      setShowForm(false)
      notification.open({
        message: 'Customers added',
        type: 'success',
      })
      await updateMigrationQueueResultTable()
    } else {
      notification.open({
        message: result.errors[0],
        type: 'error',
      })
    }

    setSubmitting(false)
  }

  return (
    <>
      <Row>
        <Col span={24}>
          <Card
            title={
              <RowWithAction
                title={translate('csi-dashboard.migration.title')}
                iconName="far fa-plus"
                onActionClick={onAddMigrationClick}
                actionButtonTitle={translate('csi-dashboard.migration.new-migration')}
              />
            }
          >
            <Row>
              <Col span={24}>
                <SearchFields translate={translate} onSearch={onSearch}></SearchFields>
              </Col>
            </Row>
            {migrationQueueResults && (
              <Row>
                <Col span={24}>
                  <h5 className="mt-16">
                    {migrationQueueResults.totalItemCount} {translate('csi-dashboard.audit-search.search-results')}
                  </h5>
                </Col>
                <Col span={13}>
                  <MigrationQueueResultsTable
                    onTableChange={onTableChange}
                    onCompanySelect={onCompanySelect}
                    loadingMigrationQueueResults={loadingMigrationQueueResults}
                    migrationQueueResults={migrationQueueResults!}
                  ></MigrationQueueResultsTable>
                </Col>
                <Col span={1}></Col>
                <Col span={10}>
                  {migrationLogsResults && (
                    <MigrationLogsTable
                      onTableChange={onLogsTableChange}
                      loadingMigrationLogsResults={loadingMigrationLogsResults}
                      migrationLogsResults={migrationLogsResults!}
                    ></MigrationLogsTable>
                  )}
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>

      <MigrationQueueForm
        visible={showForm}
        submitting={submitting}
        onCancel={() => {
          setShowForm(false)
        }}
        onSubmit={submitForm}
      ></MigrationQueueForm>
    </>
  )
}

export default Migration
